import REQ_URL from '@/consts/req-url';
import store from '@/store';
import { ApiCategoryNode, Categories, CategoriesFlat, CategoryNode } from '@/types/categories';
import { $fetch } from "./fetch";

let tree: CategoryNode[] | null = null
// Chain of nodes from the root to each node, by id
let trails = new Map<string, CategoryNode[]>()
let loading: Promise<void> | null = null
// Bumped on logout: a load started before it must not write the old tree back
let session = 0

// Counts in the tree belong to the session's region: drop it on logout so the
// next user does not see them
store.subscribe((mutation) => {
    if (mutation.type === 'setAuthStatus' && !mutation.payload) {
        tree = null
        trails = new Map()
        loading = null
        session++
    }
})

async function loadTree() {
    const startedIn = session
    const data = await $fetch<ApiCategoryNode[]>(REQ_URL.GET_CAT)
    if (startedIn !== session) return
    if (!data || !Array.isArray(data.body)) throw new Error('Некорректные данные категорий')
    tree = normalize(data.body)
    trails = new Map()
    indexTrails(tree, [])
    // Warm the SW cache: the offline category filter reads article codes from it
    fetch(REQ_URL.GET_CATEGORY_ARTICLES).catch(() => undefined)
}

export function getCategoryTree(): Promise<CategoryNode[]> {
    if (tree) return Promise.resolve(tree)
    if (!loading) {
        // After a logout a newer load may own `loading`: leave it alone
        const current: Promise<void> = loadTree()
            .catch((error) => console.error(error))
            .then(() => { if (loading === current) loading = null })
        loading = current
    }
    return loading.then(() => tree || [])
}

// Siblings may share a name: the first by id keeps the plain name as its URL
// segment, the others get their id appended
function siblingSegments(nodes: ApiCategoryNode[]) {
    const idsByName = new Map<string, string[]>()
    nodes.forEach((node) => {
        idsByName.set(node.name, [...(idsByName.get(node.name) || []), String(node.id)])
    })
    const result = new Map<string, string>()
    nodes.forEach((node) => {
        const id = String(node.id)
        const [first] = [...idsByName.get(node.name)!].sort()
        result.set(id, id === first ? node.name : `${node.name}-${id}`)
    })
    return result
}

function normalize(nodes: ApiCategoryNode[], parentHref = '/categories'): CategoryNode[] {
    const segments = siblingSegments(nodes)
    return nodes.map((node) => {
        const id = String(node.id)
        const segment = segments.get(id)!
        const href = `${parentHref}/${encodeURIComponent(segment)}`
        return {
            id,
            name: node.name,
            count: node.count,
            segment,
            href,
            children: normalize(node.children || [], href)
        }
    })
}

function indexTrails(nodes: CategoryNode[], parents: CategoryNode[]) {
    nodes.forEach((node) => {
        const trail = parents.concat(node)
        trails.set(node.id, trail)
        indexTrails(node.children, trail)
    })
}

// Categories without products in the region stay reachable by URL, but are
// left out of menus and the filter
export function listed(nodes: CategoryNode[]) {
    return nodes.filter((node) => node.count !== 0)
}

// Route segments (`$route.params.cat` split by "/") → nodes from the root.
// A name may itself contain "/", so several segments can make up one name.
export async function getCategoryTrail(segments: string[]): Promise<CategoryNode[] | undefined> {
    let level = await getCategoryTree()
    const trail: CategoryNode[] = []
    let start = 0
    while (start < segments.length) {
        let match: CategoryNode | undefined
        let end = segments.length
        while (end > start) {
            const segment = segments.slice(start, end).join('/')
            match = level.find((node) => node.segment === segment)
            if (match) break
            end--
        }
        if (!match) return undefined
        trail.push(match)
        level = match.children
        start = end
    }
    return trail
}

// Valid once the tree is loaded (getCategoryTree)
export function getCategoryTrailById(id: string): CategoryNode[] | undefined {
    return trails.get(id)
}

function toCategories(nodes: CategoryNode[]): Categories {
    return nodes.map((node) => {
        const children = listed(node.children)
        return children.length
            ? { id: node.id, name: node.name, href: node.href, children: toCategories(children) }
            : { id: node.id, name: node.name, href: node.href }
    })
}

// Autocomplete list: every listed category at every level, parents before
// their children (depth drives the indent)
function toFlat(nodes: CategoryNode[], depth = 0, path: string[] = []): CategoriesFlat {
    let items: CategoriesFlat = []
    nodes.forEach((node) => {
        const nodePath = path.concat(node.name)
        items.push({
            id: node.id,
            name: node.name,
            depth,
            group: path.join(' / '),
            pathText: nodePath.join(' / ')
        })
        items = items.concat(toFlat(listed(node.children), depth + 1, nodePath))
    })
    return items
}

export async function getCategories() {
    const nodes = listed(await getCategoryTree())
    const fullCategories: Categories = toCategories(nodes)
    const flatCategories: CategoriesFlat = toFlat(nodes)
    return { fullCategories, flatCategories }
}
